import { defineStore, type StateTree } from 'pinia';
import ID from '@compassdigital/id';
import type { DecodedID } from '@compassdigital/id/interface';
import type { MultiGroup, Multigroups } from '@compassdigital/sdk.typescript/interface/location';
import { FeatureFlags, MULTIGROUPS } from '@centric-os/types';
import { useSplitIoStore } from '@centric-os/stores';

interface State extends StateTree {
  multigroups: MultiGroup[];
}

/**
 * Pinia store for managing MultiGroups.
 */
export function useMultigroupStore(storeId = 'multigroup') {
  const store = defineStore(storeId, {
    state: (): State => ({
      multigroups: [],
    }),
    actions: {
      /**
       * Fetch MultiGroups from the API and filter them based on specific criteria.
       */
      async getMultiGroups(): Promise<void> {
        if (this.multigroups.length === 0) {
          const response = await this.cdlApi.get<Multigroups>('/location/multigroup');

          this.multigroups = this.filterMultigroups(response.data?.multigroups ?? []);
        }
      },
      /**
       * Filter MultiGroups based on name and provider.
       * @param multigroups - The list of multigroups to filter.
       * @returns The filtered list of multigroups.
       */
      filterMultigroups(multigroups: MultiGroup[]): MultiGroup[] {
        const splitIoStore = useSplitIoStore();
        const { isFeatureOn } = splitIoStore;
        const multiGroupsToExclude = new Set<string>(['BAMCO', 'BoostSelect']);
        // CF-487: Filter out "Unlisted" multigroup when INTEGRATION_AGNOSTIC_SITE feature flag is OFF
        if (!isFeatureOn(FeatureFlags.INTEGRATION_AGNOSTIC_SITE)) {
          multiGroupsToExclude.add(MULTIGROUPS.UNLISTED);
        }
        return multigroups
          .filter((m: MultiGroup) => !multiGroupsToExclude.has(m.name))
          .filter((m: MultiGroup) => {
            const decodedID: DecodedID = ID(m.id);
            return !m.name.match(/unit_test/) && decodedID.provider === 'cdl';
          });
      },
    },
    getters: {
      /**
       * Get the IDs of the multigroups as a JSON string.
       * @param state - The store state.
       * @returns A JSON string of the multigroup IDs.
       */
      getMultigroupIDs: (state): string[] => state.multigroups.map((m) => m.id),
    },
    pagination: false,
  });

  return store();
}
